/**
 * Unified settings export/import for all settings stores.
 * Produces a single JSON envelope containing postprocessing + particle settings + particle effect configs.
 * Detects legacy bare-postprocessing format and returns a descriptive error.
 */

import { usePostprocessingStore, validateSettings } from './postprocessingStore';
import type { PostprocessingSettings } from './postprocessingStore';
import { useParticleStore, validateParticleSettings } from './particleStore';
import type { ParticleSettings } from './particleStore';
import { useParticleEffectStore } from './particleEffectStore';
import type {
  ThrusterFlameConfig,
  ThrusterSparksConfig,
  AtmosphereWispsConfig,
} from '@warmancer/shared';
import { validateParticleEffectConfig, VALID_EFFECT_IDS } from '@warmancer/shared';

type EffectConfig = ThrusterFlameConfig | ThrusterSparksConfig | AtmosphereWispsConfig;

// ---- Envelope type ----

export interface SettingsEnvelope {
  postprocessing: PostprocessingSettings;
  particles: ParticleSettings;
  particleEffects?: Record<string, EffectConfig>;
}

// ---- Helpers ----

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Detect legacy bare-postprocessing format: has `bloom` at root but no `postprocessing` key.
 */
function isLegacyFormat(data: Record<string, unknown>): boolean {
  return 'bloom' in data && !('postprocessing' in data);
}

// ---- Validation ----

/**
 * Validate particle effect configs. Returns null on success, error string on failure.
 */
function validateParticleEffects(data: unknown): string | null {
  if (!isObject(data)) return 'particleEffects must be an object';

  for (const [id, config] of Object.entries(data)) {
    const error = validateParticleEffectConfig(id, config);
    if (error) return `particleEffects.${id}: ${error}`;
  }

  return null;
}

/**
 * Validate a full settings envelope. Returns null on success, error string on failure.
 */
export function validateEnvelope(data: unknown): string | null {
  if (!isObject(data)) return 'Settings must be an object';

  // Detect legacy bare-postprocessing format
  if (isLegacyFormat(data)) {
    return 'Legacy format detected: this file contains only postprocessing settings. Please re-export using the current settings panel to get the unified format.';
  }

  if (!('postprocessing' in data)) return 'Missing section: postprocessing';
  if (!('particles' in data)) return 'Missing section: particles';

  const ppError = validateSettings(data.postprocessing);
  if (ppError) return `postprocessing: ${ppError}`;

  const partError = validateParticleSettings(data.particles);
  if (partError) return `particles: ${partError}`;

  // particleEffects is optional (backward-compatible with older exports)
  if ('particleEffects' in data && data.particleEffects !== undefined) {
    const peError = validateParticleEffects(data.particleEffects);
    if (peError) return peError;
  }

  return null;
}

// ---- Export ----

/**
 * Export all settings as a JSON string containing postprocessing, particle settings,
 * and particle effect tuning configs.
 */
export function exportAllSettings(): string {
  const envelope: SettingsEnvelope = {
    postprocessing: usePostprocessingStore.getState().settings,
    particles: useParticleStore.getState().settings,
    particleEffects: useParticleEffectStore.getState().effects as Record<string, EffectConfig>,
  };
  return JSON.stringify(envelope, null, 2);
}

// ---- Import ----

/**
 * Import all settings from a JSON string. Returns error string or null on success.
 * Validates the envelope before applying to any store.
 */
export function importAllSettings(json: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return 'Invalid JSON';
  }

  const error = validateEnvelope(parsed);
  if (error) return error;

  const envelope = parsed as SettingsEnvelope;
  usePostprocessingStore.getState().applySettings(envelope.postprocessing);
  useParticleStore.getState().applySettings(envelope.particles);

  // Apply particle effect configs if present (backward-compatible)
  if (envelope.particleEffects) {
    const effectStore = useParticleEffectStore.getState();
    for (const id of VALID_EFFECT_IDS) {
      const config = envelope.particleEffects[id];
      if (config) {
        effectStore.updateEffect(id, config);
      }
    }
  }

  return null;
}
